import { ImageIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";

export type ProductMediaPlaceholderSize = "sm" | "md" | "lg";

export interface ProductMediaPlaceholderProps {
  /** `sm` es la miniatura del carrito; `lg`, la imagen grande de la ficha. */
  size?: ProductMediaPlaceholderSize;
  className?: string;
}

/**
 * El marcador "Próximamente": lo que ocupa el hueco cuando un producto no
 * tiene foto, o cuando la que tiene no carga.
 *
 * El hueco NO se deja en blanco —parece roto— sino con este marcador, que
 * dice lo que de verdad ocurre: el producto existe y la foto todavía no.
 *
 * En `sm` desaparece el texto y solo queda el icono: en una miniatura de
 * 64 px, "Próximamente" tendría que pintarse a un tamaño que no cumple el
 * contraste mínimo ni se puede leer. El icono ya comunica "aquí iba una
 * imagen", y el nombre del producto está justo al lado.
 */
const boxClasses: Record<ProductMediaPlaceholderSize, string> = {
  sm: "h-8 w-8 rounded-ds-sm",
  md: "h-14 w-14 rounded-ds-lg",
  lg: "h-20 w-20 rounded-ds-lg",
};

const iconClasses: Record<ProductMediaPlaceholderSize, string> = {
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-8 w-8",
};

export function ProductMediaPlaceholder({
  size = "md",
  className,
}: ProductMediaPlaceholderProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "absolute inset-0 flex flex-col items-center justify-center gap-3",
        "bg-gradient-to-br from-ds-mist/70 to-ds-cream-2/60",
        className,
      )}
    >
      <span
        className={cn(
          "flex items-center justify-center border border-ds-lavender-bg-2 bg-white",
          boxClasses[size],
        )}
      >
        <ImageIcon className={cn("text-ds-lavender", iconClasses[size])} aria-hidden="true" />
      </span>

      {size !== "sm" && (
        <span className="text-[10px] font-semibold uppercase tracking-ds-label text-ds-purple">
          {t("Próximamente")}
        </span>
      )}
    </div>
  );
}
